'use strict';

let layoutTimer = null;
let globalListenersBound = false;
const observedTables = new WeakSet();

// ─── Entry point ─────────────────────────────────────────────────────────────

function initComparisonTableFiveColumn() {
  processComparisonTables();
  bindGlobalListenersOnce();

  // Re-run after all resources load (fonts / images affect layout).
  window.addEventListener( 'load', processComparisonTables );
}

// ─── Main processing pass ─────────────────────────────────────────────────────

function processComparisonTables() {
  const tables = getComparisonTables();
  if ( !tables.length ) return;

  tables.forEach( buildHoverTooltips );
  syncTablesRowHeights( tables );
  tables.forEach( bindResizeObserver );
}

// ─── Listeners ────────────────────────────────────────────────────────────────

/**
 * Attach window-level listeners exactly once.
 * Done unconditionally so resize still fires even when tables arrive late.
 */
function bindGlobalListenersOnce() {
  if ( globalListenersBound ) return;
  globalListenersBound = true;

  const handler = () => scheduleLayout();
  window.addEventListener( 'resize', handler );
  window.addEventListener( 'orientationchange', handler );
}

/**
 * Attach a ResizeObserver to each table (once) so layout recalculates
 * whenever the table's own box changes — not only on window resize.
 */
function bindResizeObserver( table ) {
  if ( observedTables.has( table ) ) return;
  observedTables.add( table );

  if ( typeof ResizeObserver === 'undefined' ) return;

  const ro = new ResizeObserver( () => scheduleLayout() );
  ro.observe( table );
}

// ─── Debounced layout scheduler ───────────────────────────────────────────────

/**
 * Always re-queries the DOM so late-appearing tables are included.
 * Double rAF ensures the browser has finished painting the new layout
 * before we measure scrollWidth / getBoundingClientRect.
 */
function scheduleLayout() {
  clearTimeout( layoutTimer );
  layoutTimer = setTimeout( () => {
    requestAnimationFrame( () => {
      requestAnimationFrame( () => {
        const tables = getComparisonTables();
        if ( tables.length ) syncTablesRowHeights( tables );
      } );
    } );
  }, 100 );
}

// ─── DOM helpers ─────────────────────────────────────────────────────────────

function getComparisonTables() {
  return Array.from( document.querySelectorAll( '.e-div--table' ) );
}

function buildHoverTooltips( table ) {
  const firstColumn = table.querySelector( ':scope > .e-div' );
  if ( !firstColumn ) return;

  const rows = Array.from( firstColumn.querySelectorAll( ':scope > .e-div' ) );

  rows.forEach( ( row ) => {
    if ( row.querySelector( '.e-cmprsn__tooltip' ) ) return;

    const detail = row.querySelector( '.e-prg--dlt' );
    const detailText = detail ? detail.textContent.trim() : '';
    if ( !detailText ) return;

    const titleParagraph = row.querySelector( 'p:not(.e-prg--dlt)' );
    if ( !titleParagraph ) return;

    const trigger = document.createElement( 'span' );
    trigger.className = 'e-cmprsn__trigger';
    trigger.tabIndex = 0;
    trigger.setAttribute( 'aria-label', 'More details' );

    const icon = document.createElement( 'span' );
    icon.className = 'e-cmprsn__info';
    icon.textContent = 'i';

    const tooltip = document.createElement( 'div' );
    tooltip.className = 'e-cmprsn__tooltip';
    tooltip.textContent = detailText;
    tooltip.setAttribute( 'role', 'tooltip' );

    trigger.appendChild( icon );
    trigger.appendChild( tooltip );
    titleParagraph.appendChild( trigger );
    detail.remove();
  } );
}

// ─── Row height sync ─────────────────────────────────────────────────────────

function syncTablesRowHeights( tables ) {
  tables.forEach( ( table ) => {
    resetRowHeights( table );
    syncSingleTableRowHeights( table );
    // Measure after heights settle so scrollWidth is accurate.
    setComparisonTableWidthVar( table );
  } );
}

function resetRowHeights( table ) {
  getAllComparableCells( table ).forEach( ( cell ) => {
    cell.style.minHeight = '';
  } );
}

function syncSingleTableRowHeights( table ) {
  const columns = Array.from( table.children ).filter(
    ( node ) => node.nodeType === Node.ELEMENT_NODE
  );
  if ( columns.length < 2 ) return;

  const rowsByColumn = columns.map( getRowsForColumn );
  const maxRows = Math.max( ...rowsByColumn.map( ( rows ) => rows.length ) );

  for ( let rowIndex = 0; rowIndex < maxRows; rowIndex++ ) {
    const rowCells = rowsByColumn
      .map( ( rows ) => rows[rowIndex] )
      .filter( Boolean );
    if ( !rowCells.length ) continue;

    const maxHeight = Math.max(
      ...rowCells.map( ( cell ) => cell.getBoundingClientRect().height )
    );
    rowCells.forEach( ( cell ) => {
      cell.style.minHeight = `${ Math.ceil( maxHeight ) }px`;
    } );
  }
}

function getRowsForColumn( column ) {
  return Array.from( column.children ).filter(
    ( node ) => node.nodeType === Node.ELEMENT_NODE
  );
}

function getAllComparableCells( table ) {
  const columns = Array.from( table.children ).filter(
    ( node ) => node.nodeType === Node.ELEMENT_NODE
  );
  return columns.flatMap( ( column ) =>
    Array.from( column.children ).filter(
      ( node ) => node.nodeType === Node.ELEMENT_NODE
    )
  );
}

// ─── CSS custom property ──────────────────────────────────────────────────────

function setComparisonTableWidthVar( table ) {
  // Remove the var first — the ::after pseudo-element uses it as its width,
  // so leaving the old value would inflate scrollWidth and prevent shrinking.
  table.style.removeProperty( '--comparison-full-width' );
  // Reading offsetWidth flushes pending style recalculations before scrollWidth is measured.
  const offsetW = table.offsetWidth;
  const px = Math.ceil( Math.max( table.scrollWidth, offsetW, table.getBoundingClientRect().width ) );
  table.style.setProperty( '--comparison-full-width', `${ px }px` );
}

// ─── Bootstrap ───────────────────────────────────────────────────────────────

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', initComparisonTableFiveColumn );
} else {
  initComparisonTableFiveColumn();
}
